import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Phone, Mail, CheckCircle } from "lucide-react";
import { NAVICA, SERVICES, DEPARTMENTS } from "@/lib/constants";
import usePageMeta from "@/hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEPT_ACCENT = {
  coral: { bg: "#FCE7EB", ink: "#E2718A", label: "#E2718A" },
  orange: { bg: "#FBE5D0", ink: "#EA823A", label: "#EA823A" },
  navy: { bg: "#DCE4E6", ink: "#284952", label: "#284952" },
};

export default function Home() {
  usePageMeta({
    title: "Navica Supports, Social Work & NDIS Support Coordination",
    description: "Independent social workers providing NDIS Support Coordination, Psychosocial Recovery Coaching, DVA & DFV social work and athlete mental health across NSW, VIC and QLD.",
  });
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden pt-16 lg:pt-24 pb-20 lg:pb-28" data-testid="hero-section">
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-4xl fade-up">
            <h1 className="font-display text-6xl md:text-7xl lg:text-[8.5rem] leading-[0.98] text-[#1F2A2E] mb-8" data-testid="hero-title">
              Navigating care<br />
              with <span className="italic" style={{ color: "var(--navica-coral)" }}>YOU.</span>
            </h1>

            <div className="text-lg md:text-xl leading-relaxed text-[#4A595E] max-w-3xl mb-10 space-y-5" data-testid="hero-subtitle">
              <p>
                Navica Supports was built for the parts of life that do not fit neatly into a plan. We support people through the systems, decisions, transitions and challenges that can feel overwhelming, while keeping the person, their story and their goals at the centre.
              </p>
              <p>
                Our work spans from NDIS service provision through Support Coordination, Psychosocial Recovery Coaching, and Early Childhood supports. Employment services, employment pathways, advocacy, community connection, holistic case management and social work services. We are here to bring clarity where things feel complex, stability where things feel uncertain, and support that feels real, practical and deeply personal.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-4 mb-14">
              <Link to="/referrals" className="btn-primary" data-testid="hero-cta-refer">
                Make a Referral <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/services" className="btn-outline" data-testid="hero-cta-services">
                See how we help
              </Link>
            </div>

            <div className="grid grid-cols-3 gap-8 max-w-lg pt-8 border-t border-[#284952]/10" data-testid="hero-stats">
              {[
                { value: "AASW", label: "Aligned practice" },
                { value: "6yrs", label: "In the NDIS sector" },
                { value: "100%", label: "Person led" },
              ].map((s) => (
                <div key={s.label}>
                  <div className="font-display text-2xl md:text-3xl text-[#284952]">{s.value}</div>
                  <div className="text-[11px] uppercase tracking-[0.15em] text-[#6B7A80] mt-1.5">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Marquee strip, slim navy divider */}
      <section className="border-y border-[#284952]/10 py-4 overflow-hidden bg-[#1A363D] text-white" data-testid="marquee-strip">
        <div className="marquee-track">
          {[...Array(2)].flatMap((_, i) =>
            SERVICES.map((s) => s.title).concat(["Community Events"]).map((t) => (
              <div key={`${i}-${t}`} className="flex items-center gap-4 shrink-0">
                <span className="w-1 h-1 rounded-full bg-[#EA823A]" />
                <span className="font-display text-lg tracking-tight text-white/90">{t}</span>
              </div>
            ))
          )}
        </div>
      </section>

      {/* DEPARTMENTS, our four practice areas */}
      <section className="py-24 lg:py-32" data-testid="departments-preview">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-3xl mb-14">
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#1F2A2E] leading-[1.05] mb-6">
              Our departments.
            </h2>
            <p className="text-base md:text-lg text-[#4A595E] leading-relaxed">
              Four areas of practice, one team. Each rooted in social work, shaped by the people we walk beside.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6" data-testid="departments-grid">
            {DEPARTMENTS.map((dept, idx) => {
              const c = DEPT_ACCENT[dept.color] || DEPT_ACCENT.coral;
              return (
                <article
                  key={dept.slug}
                  className="bg-white border border-[#284952]/10 rounded-2xl p-8 lg:p-10 transition-all duration-300 hover:border-[#E2718A] flex flex-col"
                  data-testid={`dept-card-${dept.slug}`}
                >
                  <div className="flex items-start justify-between mb-5">
                    <div>
                      <span
                        className="inline-block text-[10px] uppercase tracking-[0.2em] font-semibold px-3 py-1.5 rounded-full mb-4"
                        style={{ background: c.bg, color: c.label }}
                      >
                        Department 0{idx + 1}
                      </span>
                      <h3 className="font-display text-3xl md:text-4xl text-[#1F2A2E] leading-tight">{dept.name}</h3>
                    </div>
                  </div>

                  <p className="text-[#4A595E] leading-relaxed mb-6 italic">{dept.tagline}</p>

                  <ul className="space-y-4 mb-6 flex-1">
                    {dept.services.map((s) => (
                      <li key={s.title} className="flex gap-3">
                        <span className="mt-2 shrink-0 w-1.5 h-1.5 rounded-full" style={{ background: c.ink }} />
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold text-[#1F2A2E] leading-snug">{s.title}</span>
                            {s.upcoming && (
                              <span className="text-[9px] uppercase tracking-[0.15em] font-bold px-2 py-0.5 rounded-full bg-[#F1E4D5] text-[#6B7A80]" data-testid="upcoming-pill">
                                Upcoming
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-[#4A595E] leading-relaxed">{s.short}</div>
                        </div>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to="/services"
                    className="inline-flex items-center gap-2 text-sm font-semibold group mt-auto"
                    style={{ color: c.ink }}
                    data-testid={`dept-link-${dept.slug}`}
                  >
                    Explore {dept.name} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </article>
              );
            })}
          </div>

          <div className="mt-12 text-center">
            <Link to="/services" className="btn-outline" data-testid="all-services-link">
              View all services <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* QUICK REFERRAL, inline lead capture */}
      <QuickReferral />

      {/* CTA STRIP */}
      <section className="py-24 lg:py-28 border-t border-[#284952]/10 bg-[#F1E4D5]" data-testid="cta-strip">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#1F2A2E] leading-[1.05] mb-6">
            Take the next step <span className="italic" style={{ color: "var(--navica-coral)" }}>with us</span>.
          </h2>
          <p className="text-lg text-[#4A595E] leading-relaxed mb-10 max-w-2xl mx-auto">
            Participant, family member, or fellow professional. Reach out, and we&apos;ll come back to you within one business day.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a href={`tel:${NAVICA.phoneRaw}`} className="btn-primary" data-testid="cta-call">
              <Phone className="w-4 h-4" /> {NAVICA.phone}
            </a>
            <a href={`mailto:${NAVICA.email}`} className="btn-outline" data-testid="cta-email">
              <Mail className="w-4 h-4" /> {NAVICA.email}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

/** Quick inline referral form, 5 fields, submits to /api/referrals */
function QuickReferral() {
  const [form, setForm] = useState({
    referral_type: "ndis",
    referral_source: "",
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    reason_for_referral: "",
    consent: false,
  });
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.consent) {
      setStatus({ state: "error", message: "Please confirm consent to proceed." });
      return;
    }
    if (!form.first_name.trim() || !form.last_name.trim()) {
      setStatus({ state: "error", message: "Please add a first and last name." });
      return;
    }
    setStatus({ state: "submitting", message: "" });
    try {
      const { consent, ...payload } = form;
      void consent;
      await axios.post(`${API}/referrals`, payload);
      setStatus({ state: "success", message: "Thanks, we'll be in touch within one business day." });
      setForm({ referral_type: "ndis", referral_source: "", first_name: "", last_name: "", phone: "", email: "", reason_for_referral: "", consent: false });
    } catch (err) {
      setStatus({ state: "error", message: "Something went wrong. Please try again or call us directly." });
    }
  }

  if (status.state === "success") {
    return (
      <section className="py-24 lg:py-28 bg-[#F1E4D5] border-t border-[#284952]/10" data-testid="quick-referral">
        <div className="max-w-2xl mx-auto px-5 lg:px-8 text-center">
          <CheckCircle className="w-14 h-14 text-[#E2718A] mx-auto mb-6" />
          <h2 className="font-display text-4xl md:text-5xl text-[#1F2A2E] leading-[1.05] mb-4">
            Referral received.
          </h2>
          <p className="text-lg text-[#4A595E] leading-relaxed mb-6">{status.message}</p>
          <Link to="/referrals" className="text-[#E2718A] font-semibold underline underline-offset-4">
            Send a more detailed referral →
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 lg:py-32 bg-[#F1E4D5] border-t border-[#284952]/10" data-testid="quick-referral">
      <div className="max-w-5xl mx-auto px-5 lg:px-8 grid lg:grid-cols-5 gap-12">
        <div className="lg:col-span-2">
          <h2 className="font-display text-4xl md:text-5xl text-[#1F2A2E] leading-[1.05] mb-5">
            Send a quick referral.
          </h2>
          <p className="text-base md:text-lg text-[#4A595E] leading-relaxed mb-4">
            Fill in a few details and we&apos;ll come back to you within one business day. Nothing formal yet, this is just to start a conversation.
          </p>
          <p className="text-sm text-[#6B7A80]">
            Prefer to send everything upfront? <Link to="/referrals" className="text-[#E2718A] font-semibold underline underline-offset-2">Use the full referral form →</Link>
          </p>
        </div>

        <form className="lg:col-span-3 bg-white rounded-2xl border border-[#284952]/10 p-6 lg:p-8 space-y-5" onSubmit={handleSubmit} data-testid="quick-referral-form">
          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-2">
              What are you looking for?
            </label>
            <select
              className="input-navica"
              value={form.referral_type}
              onChange={(e) => update("referral_type", e.target.value)}
              data-testid="qr-type"
            >
              <option value="ndis">NDIS support (coordination, PSRC, employment, ECA)</option>
              <option value="dva">DVA / veteran wellbeing</option>
              <option value="dfv">Domestic & family violence support</option>
              <option value="athlete">Athlete mental health (mind & motion)</option>
              <option value="other">Something else</option>
            </select>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-2">
              Where is the person right now? <span className="normal-case text-[#6B7A80]/70">(optional)</span>
            </label>
            <select
              className="input-navica"
              value={form.referral_source}
              onChange={(e) => update("referral_source", e.target.value)}
              data-testid="qr-source"
            >
              <option value="">- Select if you&apos;d like -</option>
              <option value="Community">In the community / at home</option>
              <option value="Hospital">In hospital</option>
              <option value="Custody">In custody / correctional facility</option>
              <option value="Rehab">In a rehab or residential facility</option>
              <option value="Homelessness">Sleeping rough / homelessness</option>
              <option value="Other">Somewhere else</option>
            </select>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-2">First name</label>
              <input className="input-navica" value={form.first_name} onChange={(e) => update("first_name", e.target.value)} required data-testid="qr-first" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-2">Last name</label>
              <input className="input-navica" value={form.last_name} onChange={(e) => update("last_name", e.target.value)} required data-testid="qr-last" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-2">Phone</label>
              <input type="tel" className="input-navica" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="04XX XXX XXX" data-testid="qr-phone" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-2">Email</label>
              <input type="email" className="input-navica" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@example.com" data-testid="qr-email" />
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-2">Tell us a little about what you need</label>
            <textarea
              rows={4}
              className="input-navica"
              value={form.reason_for_referral}
              onChange={(e) => update("reason_for_referral", e.target.value)}
              placeholder="A rough description is plenty, we'll ask the rest when we speak."
              data-testid="qr-reason"
            />
          </div>

          <label className="flex items-start gap-3 text-sm text-[#4A595E]">
            <input
              type="checkbox"
              className="mt-1 navica-check"
              checked={form.consent}
              onChange={(e) => update("consent", e.target.checked)}
              data-testid="qr-consent"
            />
            <span>
              I consent to Navica Supports contacting me about this enquiry. We handle your information in line with our <Link to="/privacy" className="text-[#E2718A] underline">Privacy Policy</Link>.
            </span>
          </label>

          {status.state === "error" && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3" data-testid="qr-error">
              {status.message}
            </div>
          )}

          <button
            type="submit"
            className="btn-primary w-full sm:w-auto"
            disabled={status.state === "submitting"}
            data-testid="qr-submit"
          >
            {status.state === "submitting" ? "Sending…" : "Send referral"} <ArrowRight className="w-4 h-4" />
          </button>
        </form>
      </div>
    </section>
  );
}
